import React from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { getUID, sendNotfication } from '../../../Helper';
import { launchHuiduGame } from '../../../services/huiduGameService';
import { useGameLaunch } from '../../../Context/GameLaunchContext';

const Section = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 0;
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
`;

const BottomRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;
  }
`;

const LargeCard = styled.div`
  background: #2a2f30;
  border-radius: 16px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 140px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &:hover {
    transform: translateY(-3px) scale(1.01);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    min-height: 110px;
    padding: 12px;
    align-items: center;
    justify-content: flex-start;
  }
`;

const LargeCardImage = styled.img`
  position: absolute;
  right: -10px;
  bottom: -10px;
  height: 130px;
  width: auto;
  object-fit: contain;
  pointer-events: none;

  @media (max-width: 768px) {
    position: relative;
    right: auto;
    bottom: auto;
    height: 110px;
    margin-top: 8px;
  }
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CardIcon = styled.img`
  width: 24px;
  height: 24px;

  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
  }
`;

const CardDescription = styled.p`
  color: #9ca3af;
  font-size: 13px;
  margin: 8px 0 0 0;
  max-width: 60%;
  line-height: 1.4;
  z-index: 1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const SmallCard = styled.div`
  background: #2a2f30;
  border-radius: 14px;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-width: 0;

  &:hover {
    transform: translateY(-3px) scale(1.03);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    padding: 10px 6px;
    gap: 6px;
    border-radius: 10px;
  }
`;

const SmallCardImage = styled.img`
  width: 70px;
  height: 70px;
  object-fit: contain;

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
  }
`;

const SmallCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const SmallIcon = styled.img`
  width: 16px;
  height: 16px;
`;

// Casino icon SVG inline
const CasinoIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="#25ee89"/>
    <path d="M9 8h2v8H9V8zm4 2h2v6h-2v-6z" fill="#fff"/>
  </svg>
);

const SportsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="#25ee89"/>
    <path d="M12 6v12M6 12h12" stroke="#fff" strokeWidth="2"/>
  </svg>
);

function GamingCards() {
  const history = useHistory();
  const { setGameUrl, setIsGameOpen, setIsLuckySportGame } = useGameLaunch();

  const handleNavigate = (path) => {
    history.push(path);
  };

  const handleSportsClick = async () => {
    try {
      const id = getUID();
      if (!id) {
        sendNotfication('Please login to play Sports', 'error');
        return;
      }
      const credits = localStorage.getItem('credit') || '0';
      const coins = localStorage.getItem('coin') || 'INR';
      const result = await launchHuiduGame(
        '92b24e4c25107367a80e0fe1a97c24e4',
        id,
        credits,
        coins
      );
      if (result && result.game_launch_url) {
        setGameUrl(result.game_launch_url);
        setIsLuckySportGame(true);
        setIsGameOpen(true);
      } else {
        sendNotfication('Failed to launch Sports', 'error');
      }
    } catch (error) {
      console.error('Failed to launch Sports game:', error);
      sendNotfication(error.message || 'Failed to launch Sports', 'error');
    }
  };

  return (
    <Section>
      <TopRow>
        <LargeCard onClick={() => handleNavigate('/casino')}>
          <div>
            <CardTitle>
              <CardIcon src="/assets/images/icons/casino_icon.png" alt="" onError={(e) => { e.target.style.display = 'none'; }} />
              CASINO
            </CardTitle>
            <CardDescription>
              Dive into our in-house games, live casino and slots
            </CardDescription>
          </div>
          <LargeCardImage 
            src="/assets/pics-home/casino.png" 
            alt="Casino"
          />
        </LargeCard>

        <LargeCard onClick={handleSportsClick}>
          <div>
            <CardTitle>
              <CardIcon src="/assets/images/icons/sports_icon.png" alt="" onError={(e) => { e.target.style.display = 'none'; }} />
              SPORTS
            </CardTitle>
            <CardDescription>
              Bet on Football, Cricket, NFL, eSports & over 80 sports!
            </CardDescription>
          </div>
          <LargeCardImage 
            src="/assets/pics-home/sports.png" 
            alt="Sports"
          />
        </LargeCard>
      </TopRow>

      <BottomRow>
        <SmallCard onClick={async () => {
          const id = getUID();
          if (!id) { sendNotfication('Please login to play Poker', 'error'); return; }
          try {
            const credits = localStorage.getItem('credit') || '0';
            const coins = localStorage.getItem('coin') || 'INR';
            const result = await launchHuiduGame('146896890ee326c1f808ef747dd962cf', id, credits, coins);
            if (result && result.game_launch_url) { setGameUrl(result.game_launch_url); setIsGameOpen(true); }
            else { sendNotfication('Failed to launch E-Sports', 'error'); }
          } catch (e) { sendNotfication(e.message || 'Failed to launch E-Sports', 'error'); }
        }}>
          <SmallCardImage 
            src="/assets/pics-home/poker.png" 
            alt="Poker"
          />
          <SmallCardTitle>
            POKER
          </SmallCardTitle>
        </SmallCard>

        <SmallCard onClick={() => handleNavigate('/spribe-games')}>
          <SmallCardImage 
            src="/assets/pics-home/racing.png" 
            alt="Spribe"
          />
          <SmallCardTitle>
            Spribe
          </SmallCardTitle>
        </SmallCard>

        <SmallCard onClick={async () => {
          const id = getUID();
          if (!id) { sendNotfication('Please login to play E-Sports', 'error'); return; }
          try {
            const credits = localStorage.getItem('credit') || '0';
            const coins = localStorage.getItem('coin') || 'INR';
            const result = await launchHuiduGame('4ee8e0051a035b463b47c3c473ce317d', id, credits, coins);
            if (result && result.game_launch_url) { setGameUrl(result.game_launch_url); setIsGameOpen(true); }
            else { sendNotfication('Failed to launch E-Sports', 'error'); }
          } catch (e) { sendNotfication(e.message || 'Failed to launch E-Sports', 'error'); }
        }}>
          <SmallCardImage 
            src="/assets/pics-home/ninja.png" 
            alt="E-Sports"
          />
          <SmallCardTitle>
            E-Sports
          </SmallCardTitle>
        </SmallCard>

        <SmallCard onClick={async () => {
          const id = getUID();
          if (!id) { sendNotfication('Please login to play UpDown', 'error'); return; }
          try {
            const credits = localStorage.getItem('credit') || '0';
            const coins = localStorage.getItem('coin') || 'INR';
            const result = await launchHuiduGame('699fff17c56ac4af07db510e86cdf6bb', id, credits, coins, 'en', 'EVOASIA');
            if (result && result.game_launch_url) { setGameUrl(result.game_launch_url); setIsGameOpen(true); }
            else { sendNotfication('Failed to launch UpDown', 'error'); }
          } catch (e) { sendNotfication(e.message || 'Failed to launch UpDown', 'error'); }
        }}>
          <SmallCardImage 
            src="/assets/pics-home/updown.png" 
            alt="Updown"
          />
          <SmallCardTitle>
            UPDOWN
          </SmallCardTitle>
        </SmallCard>

        <SmallCard onClick={() => handleNavigate('/pm-slots')}>
          <SmallCardImage 
            src="/assets/pics-home/bingo.png" 
            alt="Bingo"
          />
          <SmallCardTitle>
            SLOTS
          </SmallCardTitle>
        </SmallCard>
      </BottomRow>
    </Section>
  );
}

export default GamingCards;
